import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Table, Button, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { quotationsApi } from '../services/api';
import { CotacaoComTendenciaDto } from '../types';

const QuotationsList: React.FC = () => {
  const [quotations, setQuotations] = useState<CotacaoComTendenciaDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<number | null>(null);

  useEffect(() => {
    fetchQuotations();
  }, []);

  const fetchQuotations = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await quotationsApi.getAll();
      setQuotations(data);
    } catch (err) {
      setError('Erro ao carregar cotações');
      console.error('Error fetching quotations:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTrendLabel = (trend: 'up' | 'down' | 'stable' | null) => {
    switch (trend) {
      case 'up': return '↑ Alta';
      case 'down': return '↓ Baixa';
      case 'stable': return '→ Estável';
      default: return '-';
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Excluir esta cotação?')) return;
    setDeleting(id);
    try {
      await quotationsApi.delete(id);
      setQuotations(prev => prev.filter(q => q.idCotacao !== id));
    } catch (err) {
      setError('Erro ao excluir cotação');
    } finally {
      setDeleting(null);
    }
  };

  const getTrendClass = (trend: 'up' | 'down' | 'stable' | null) => {
    switch (trend) {
      case 'up': return 'text-success';
      case 'down': return 'text-danger';
      case 'stable': return 'text-muted';
      default: return '';
    }
  };

  if (loading) {
    return (
      <Container className="mt-4">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <h2>Gerenciar Cotações</h2>
            <div>
              <Link to="/admin/quotations/new" className="btn btn-primary">
                <i className="bi bi-plus-circle me-2"></i>
                Nova Cotação
              </Link>
            </div>
          </div>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {quotations.length === 0 ? (
        <Alert variant="info">
          Nenhuma cotação cadastrada. Clique em "Nova Cotação" para adicionar.
        </Alert>
      ) : (
        <div className="table-responsive">
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Data/Hora</th>
                <th>Soja (R$/sc 60kg)</th>
                <th>Tendência</th>
                <th>Arroz (R$/sc 50kg)</th>
                <th>Tendência</th>
                <th>Milho (R$/sc 60kg)</th>
                <th>Tendência</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {quotations.map((quotation) => (
                <tr key={quotation.idCotacao}>
                  <td>{formatDate(quotation.dataCadastro)}</td>
                  <td>R$ {quotation.soja.toFixed(2)}</td>
                  <td className={getTrendClass(quotation.tendenciaSoja)}>
                    {getTrendLabel(quotation.tendenciaSoja)}
                  </td>
                  <td>R$ {quotation.arroz.toFixed(2)}</td>
                  <td className={getTrendClass(quotation.tendenciaArroz)}>
                    {getTrendLabel(quotation.tendenciaArroz)}
                  </td>
                  <td>R$ {quotation.milho.toFixed(2)}</td>
                  <td className={getTrendClass(quotation.tendenciaMilho)}>
                    {getTrendLabel(quotation.tendenciaMilho)}
                  </td>
                  <td>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      disabled={deleting === quotation.idCotacao}
                      onClick={() => handleDelete(quotation.idCotacao)}
                    >
                      {deleting === quotation.idCotacao
                        ? <span className="spinner-border spinner-border-sm" />
                        : <i className="bi bi-trash"></i>
                      }
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      <div className="mt-3 text-muted">
        <small>
          <i className="bi bi-info-circle me-2"></i>
          Total de {quotations.length} cotação(ões) cadastrada(s).
          A cotação mais recente é exibida na página inicial.
        </small>
      </div>
    </Container>
  );
};

export default QuotationsList;
